// Health watch banner: shown at the top of Today, Health and the sitter
// guide when what's been logged adds up to "call the vet". See
// src/domain/healthwatch.ts for the rules.

import { NotebookPen, Phone, ShieldAlert, Siren, Stethoscope, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { LOCAL_CONTACTS } from '../../content/local.ts';
import { useCollection, useDevice, useEvents, useNow, useProfile } from '../../data/hooks.ts';
import { LEVEL_LABEL, healthWatch, type HealthFlag } from '../../domain/healthwatch.ts';
import { navigate } from '../../app/router.ts';
import { openVetVisit } from './VetVisits.tsx';

const STORE = 'arlo.healthwatch.dismissed';

function readDismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORE) ?? '[]') as string[];
  } catch {
    return [];
  }
}

export function useHealthFlags(): HealthFlag[] {
  const events = useEvents();
  const care = useCollection('care');
  const profile = useProfile();
  const now = useNow(60_000);
  return useMemo(() => healthWatch({ events, care, profile, now }), [events, care, profile, now]);
}

export function HealthWatchBanner() {
  const flags = useHealthFlags();
  const contacts = useCollection('contacts');
  const guest = !!useDevice().guest;
  const [dismissed, setDismissed] = useState(readDismissed);
  // "Call the vet now" can't be dismissed — it goes when the logs calm down.
  const shown = flags.filter((f) => f.level === 'vet-now' || !dismissed.includes(`${f.key}:${f.level}`));
  if (!shown.length) return null;

  const vet = contacts.find((c) => c.role === 'vet' && c.phone) ?? contacts.find((c) => c.role === 'emergency-vet' && c.phone);
  const outOfHours = contacts.find((c) => c.role === 'emergency-vet' && c.phone) ?? LOCAL_CONTACTS.find((c) => c.role === 'emergency-vet' && c.phone);
  const hour = new Date().getHours();
  const afterHours = hour < 8 || hour >= 18;
  const call = afterHours && outOfHours ? outOfHours : (vet ?? outOfHours);

  const dismiss = (f: HealthFlag) => {
    const next = [...dismissed, `${f.key}:${f.level}`].slice(-50);
    setDismissed(next);
    try {
      localStorage.setItem(STORE, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="healthwatch" role="alert">
      {shown.map((f) => (
        <div key={f.key} className={`callout healthwatch-flag tone-${f.level === 'vet-now' ? 'bad' : f.level === 'vet-today' ? 'warn' : 'info'}`}>
          {f.level === 'vet-now' ? <Siren aria-hidden /> : f.level === 'vet-today' ? <Stethoscope aria-hidden /> : <ShieldAlert aria-hidden />}
          <div>
            <span className="eyebrow">Health watch · {LEVEL_LABEL[f.level]}</span>
            <strong>{f.title}</strong>
            <span className="small">{f.evidence}</span>
            <span className="small">{f.advice}</span>
            <div className="row healthwatch-actions">
              {f.level !== 'watch' && call?.phone && (
                <a className="btn btn-primary btn-sm" href={`tel:${call.phone.replace(/\s/g, '')}`}>
                  <Phone className="btn-icon" aria-hidden />
                  <span>Call {call.name}</span>
                </a>
              )}
              {f.level !== 'watch' && (
                <button type="button" className="btn btn-soft btn-sm healthwatch-emergency" onClick={() => navigate('/emergency')}>
                  <Siren className="btn-icon" aria-hidden />
                  <span>Emergency</span>
                </button>
              )}
              {f.level !== 'watch' && !guest && (
                <button
                  type="button"
                  className="btn btn-soft btn-sm"
                  onClick={() => {
                    // Write down what they said — and stop nagging about this one.
                    dismiss(f);
                    openVetVisit({ kind: 'call', reason: `${f.title} — ${f.evidence}`, contactId: call && 'id' in call ? (call.id as string) : undefined, practice: call?.name });
                  }}
                >
                  <NotebookPen className="btn-icon" aria-hidden />
                  <span>Spoken to the vet</span>
                </button>
              )}
              {(f.level === 'watch' || (guest && f.level === 'vet-today')) && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => dismiss(f)}>
                  <X className="btn-icon" aria-hidden />
                  <span>{f.level === 'watch' ? 'Noted' : 'Spoken to the vet'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
